import { preventPopoverCloseOnSpacebarPress, stopPropagation } from '@/_shared/tools/event-prevention.js';
import { Button, Input } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import type { Column } from '@tanstack/react-table';
import { type ChangeEvent, useCallback, useRef } from 'react';
import { resetFilter } from '../../_shared/reset.js';
import { useSharedStyles } from '../../shared-styles.js';
import { typedSetStringArrayColumnFilterValue } from '../filter/index.js';
import type { StringArrayFilterValuesWithoutInArray } from '../filter/operation.js';
import type { StringArrayFilterSchemaWithoutInArray } from '../filter/schema.js';

type StringArrayFilterProps<TData, TValue> = {
    column: Column<TData, TValue>;
    dropdownValue: StringArrayFilterValuesWithoutInArray['id'];
};

export function StringArrayFilter<TData, TValue>({ column, dropdownValue }: StringArrayFilterProps<TData, TValue>) {
    const sharedStyles = useSharedStyles();

    const filter = column.getFilterValue() as StringArrayFilterSchemaWithoutInArray | undefined;
    const value = filter && 'value' in filter ? (filter.value ?? '') : '';

    const inputRef = useRef<HTMLInputElement>(null);

    const onValueChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            if (value.length) {
                typedSetStringArrayColumnFilterValue(column, {
                    operation: dropdownValue,
                    value,
                });
            } else {
                resetFilter(column);
            }
        },
        [column, dropdownValue]
    );

    const clearValueInput = useCallback(() => {
        resetFilter(column);
        inputRef.current?.focus();
    }, [column]);

    return (
        <Input
            ref={inputRef}
            className={sharedStyles.input}
            placeholder="Filtern..."
            value={value}
            onChange={onValueChange}
            onMouseDown={stopPropagation}
            onKeyDown={preventPopoverCloseOnSpacebarPress}
            contentAfter={
                <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={clearValueInput} />
            }
        />
    );
}
